import React from 'react';
import styled from 'styled-components';
import { Icon, Input, message, Popover, Progress } from 'antd';
import { uploadHelper } from '../../help/upload';

import FileChunk from '../../fileChunk/fileChunk';
import {
  completeUpload,
  CreateSignResponse,
  createSigns,
  getExtension,
} from 'common/components/resourceUpload/_helpers/file-chunks-upload';

const ResourceSizeLimit: number = 10 * 1024 * 1024; // 10M

const File = styled(Input)`
  width: 60%;
  margin-right: 16px;
`;

export interface LineUploaderProps extends React.Props<undefined> {
  file: File;
  id: string;
  name: string;
  type: any;
  url: string;
  sessionId: string;
  subject: 'chinese' | 'english';
  onNameChange: (value: string, id: string) => void;
  onFileCancel: (id: string) => void;
  onUUIDChange: (oldId: string, newId: string) => void;
  onUrlChange: (id: string, url: string) => void;
  onUploadStart: () => void;
  onUploadEnd: () => void;
}

interface LineUploaderState {
  progress: number;
  reviewUrl: string;
  status: 'success' | 'failed' | 'uploading' | 'idle';
}

interface UploadResultInterface {
  successList: any[];
  failList: any[];
  result: any;
}

interface ResourseUploadInterface {
  file: File;
  chunkIndex: number;
  chunksCount: number;
  sha256: string | number;
  name: string;
  suffix: string;
  index: number;
}

export class LineUploader extends React.Component<LineUploaderProps, LineUploaderState> {
  private xhr: XMLHttpRequest;

  private uploadResult: UploadResultInterface = {
    successList: [],
    failList: [],
    result: null,
  };

  private fileChunk: any;
  private signResponse: CreateSignResponse;
  private responseETags: string[] = [];
  private sha256 = '';

  constructor(props: LineUploaderProps) {
    super(props);

    this.state = {
      progress: 0,
      reviewUrl: '',
      status: 'uploading',
    };

    const { name } = this.props;
    const nameParts = name.split('.');
    if (nameParts.length !== 2) {
      message.error('上传失败, 文件名中不能包含多个点号');
      this.cancelHandle();
      return;
    }

    this.checkSha256()
      .then((res: any) => {
        const { url, uuid } = res;
        if (!url && !uuid) {
          const { size } = props.file;
          size > ResourceSizeLimit ? this.bigResourceUpload() : this.uploadFile();
        } else if (!!url && !!uuid) {
          this.props.onUrlChange(this.props.id, url);
          this.props.onUUIDChange(this.props.id, uuid);
          this.setProgress(100);
          this.setState({
            status: 'success',
          });
        }
      })
      .catch((err: any) => {
        if (err.message === 'ResourceExisted') {
          this.props.onFileCancel(this.props.id);
          message.error('上传失败, 资源已经存在');
        } else {
          this.setState({
            status: 'failed',
          });
        }
      });
  }

  public componentWillUnmount() {
    if (this.xhr && this.xhr.abort) {
      this.xhr.abort();
    }
  }

  public checkSha256 = () => {
    const { file } = this.props;
    const { name } = file;
    this.fileChunk = new FileChunk(file, 1024 * 1024 * 5);
    return this.fileChunk.init().then((sha256: string) => {
      this.sha256 = String(sha256);
      return this.verifySha256(sha256, name);
    });
  };

  public verifySha256 = (sha256: string, name: string) => {
    const sessionId = this.props.sessionId + nx.id();
    return alo7Api.fetch(
      `${alo7Api.prefix.host}/api/v1/resources_${this.props.subject}/check_hash` +
      alo7Nav.objectToSearchString({ sha256, sessionId, name }),
    );
  };

  public bigResourceUpload = () => {
    const { fileList, fileHash } = this.fileChunk;
    const { name, type } = this.props;
    const suffix = getExtension(name);
    const uploadType = type.toUpperCase();
    const multipartQuantity = fileList.length;
    const signOptions = { suffix, uploadType, multipartQuantity };
    createSigns(signOptions).then((signResponse) => {
      this.signResponse = signResponse;
      this.uploadResource(fileList, fileHash);
    });
  };

  public uploadResourceRequest = (params: ResourseUploadInterface) => {
    const { file, index } = params;
    const url = this.signResponse.uploadUrls[index];
    return fetch(url, { method: 'PUT', body: file })
      .then((res: any) => {
        const etag = res.headers.get('etag');
        this.responseETags[index] = etag.replace(/^"|"$/g, '');
        this.uploadResult.successList.push(params);
        this.setProgress(
          Math.floor((this.uploadResult.successList.length / this.fileChunk.chunksCount) * 100),
        );
        this.uploadResult = {
          ...this.uploadResult,
          result: res,
        };
        return res;
      })
      .catch(() => {
        this.uploadResult.failList.push(params);
        this.setProgress(
          Math.floor((this.uploadResult.successList.length / this.fileChunk.chunksCount) * 100),
        );
      });
  };

  public uploadResource = async (fileList: any[], sha256: string) => {
    try {
      const promises = fileList.map((fileInfo, index) =>
        this.uploadResourceRequest({
          ...fileInfo,
          index,
          sha256,
          sessionId: this.props.sessionId,
        }),
      );
      await Promise.all(promises);
    } catch (err) {
      if (err === 'ResourceExisted') {
        this.props.onFileCancel(this.props.id);
      } else {
        this.setState({
          status: 'failed',
        });
      }
    } finally {
      const { successList, failList } = this.uploadResult;
      const suffix = this.props.name.split('.')[1];
      if (successList.length === this.fileChunk.chunksCount && failList.length === 0) {
        completeUpload({
          uploadType: this.props.type.toUpperCase(),
          suffix,
          eTags: this.responseETags,
          uuid: this.signResponse.uuid,
          name: this.props.name,
          sizeInByte: this.props.file.size,
          sha256: this.sha256,
        }).then((res) => {
          this.props.onUrlChange(this.props.id, res?.url);
          this.props.onUUIDChange(this.props.id, res?.uuid);
          this.setState({
            status: 'success',
          });
        });
      }
    }
  };

  public uploadFile() {
    this.xhr = uploadHelper(
      {
        file: this.props.file,
        type: this.props.type.toUpperCase(),
        updateProgress: this.setProgress,
        subject: this.props.subject,
        sessionId: this.props.sessionId,
      },
      (res: any) => {
        this.props.onUrlChange(this.props.id, res.url);
        this.props.onUUIDChange(this.props.id, res.uuid);
        this.setState({
          status: 'success',
        });
      },
      (code: string) => {
        if (code === 'ResourceExisted') {
          this.props.onFileCancel(this.props.id);
        } else {
          this.setState({
            status: 'failed',
          });
        }
      },
    );
  }

  public setProgress = (value: number) => {
    this.setState({
      progress: value,
    });
    if (value < 10) {
      this.props.onUploadStart();
    }
    if (value > 98) {
      this.props.onUploadEnd();
    }
  };

  public cancelHandle = () => {
    if (this.xhr && this.xhr.abort) {
      this.xhr.abort();
    }
    this.props.onFileCancel(this.props.id);
  };

  public render() {
    const { status, progress } = this.state;
    const { name, onNameChange, id, children } = this.props;
    return (
      <Popover
        placement={'bottom'}
        content={<div style={{ maxWidth: '400px', wordBreak: 'break-all' }}>{name}</div>}>
        <div
          style={{
            width: '100%',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
          }}>
          <File
            value={name.replace(/\.[^.]+$/, '')}
            onChange={e => onNameChange(e.target.value + name.match(/\.[^.]+$/)[0], id)}
          />
          {status === 'uploading' && (
            <Progress
              style={{ width: '20%', position: 'relative', top: '-2px' }}
              percent={progress}
              showInfo={false}
              strokeColor={alo7Theme.primaryColor}
              type={'line'}
            />
          )}
          {status === 'failed' && (
            <div
              style={{
                width: '20%',
                color: '#F44336',
                textAlign: 'center',
                fontSize: '12px',
              }}>
              上传失败
            </div>
          )}
          <Icon
            onClick={this.cancelHandle}
            type={'close'}
            style={{ margin: '0 16px', cursor: 'pointer' }}
          />
          {status === 'failed' && (
            <span
              onClick={() => {
                this.setState({
                  status: 'uploading',
                });
                this.uploadFile();
              }}
              style={{ cursor: 'pointer', fontSize: '12px' }}>
              重试
            </span>
          )}
          {status === 'success' && (
            <div
              style={{
                width: '20%',
                color: alo7Theme.primaryColor,
                textAlign: 'center',
                fontSize: '12px',
              }}>
              上传成功
            </div>
          )}
          {status === 'success' && children}
        </div>
      </Popover>
    );
  }
}
